import { Preferences } from '@capacitor/preferences';
import { Capacitor } from '@capacitor/core';
import { BiometricAuth } from '@aparajita/capacitor-biometric-auth';
import { auth } from './firebase';
import { onAuthStateChanged } from 'firebase/auth';

const CREDENTIALS_KEY = 'firebase_credentials';
const BIOMETRIC_ENABLED_KEY = 'biometric_enabled';

/**
 * Check if device supports biometric authentication
 */
export const checkBiometricAvailability = async () => {
  if (!Capacitor.isNativePlatform()) {
    return { available: false, reason: 'Not a native platform' };
  }

  try {
    const result = await BiometricAuth.checkBiometry();
    return {
      available: result.isAvailable,
      biometryType: result.biometryType,
      reason: result.reason
    };
  } catch (error) {
    console.error('Error checking biometric availability:', error);
    return { available: false, reason: 'Error checking' };
  }
};

/**
 * Save credentials securely with biometric protection
 */
export const saveBiometricCredentials = async (email: string, password: string) => {
  if (!Capacitor.isNativePlatform()) {
    return;
  }

  try {
    // Encrypt and save credentials
    const credentials = JSON.stringify({ email, password });
    await Preferences.set({
      key: CREDENTIALS_KEY,
      value: credentials,
    });

    await Preferences.set({
      key: BIOMETRIC_ENABLED_KEY,
      value: 'true',
    });

    console.log('🔐 Credentials saved with biometric protection');
  } catch (error) {
    console.error('Failed to save biometric credentials:', error);
  }
};

/**
 * Retrieve credentials using biometric authentication
 */
export const getBiometricCredentials = async (): Promise<{ email: string; password: string } | null> => {
  if (!Capacitor.isNativePlatform()) {
    return null;
  }

  try {
    const { value: enabled } = await Preferences.get({ key: BIOMETRIC_ENABLED_KEY });
    if (enabled !== 'true') {
      return null;
    }

    // Check biometric availability first
    const availability = await checkBiometricAvailability();
    if (!availability.available) {
      console.log('ℹ️ Biometric not available:', availability.reason);
      return null;
    }

    // Authenticate with biometrics
    try {
      await BiometricAuth.authenticate({
        reason: 'Sign in to Wonderstood',
        cancelTitle: 'Cancel',
        allowDeviceCredential: true,
        iosFallbackTitle: 'Use Passcode',
      });
    } catch (error) {
      console.log('❌ Biometric authentication failed or cancelled');
      return null;
    }

    // Retrieve saved credentials
    const { value } = await Preferences.get({ key: CREDENTIALS_KEY });
    if (!value) {
      return null;
    }

    const credentials = JSON.parse(value);
    console.log('✅ Biometric authentication successful');
    return credentials;
  } catch (error) {
    console.error('Failed to get biometric credentials:', error);
    return null;
  }
};

/**
 * Clear saved biometric credentials
 */
export const clearBiometricCredentials = async () => {
  if (!Capacitor.isNativePlatform()) {
    return;
  }

  await Preferences.remove({ key: CREDENTIALS_KEY });
  await Preferences.remove({ key: BIOMETRIC_ENABLED_KEY });
  console.log('🗑️ Biometric credentials cleared');
};

/**
 * Check if user has biometric login enabled
 */
export const isBiometricEnabled = async (): Promise<boolean> => {
  if (!Capacitor.isNativePlatform()) {
    return false;
  }

  const { value } = await Preferences.get({ key: BIOMETRIC_ENABLED_KEY });
  return value === 'true';
};

/**
 * Initialize auth persistence - restore session on app launch using biometrics
 * Note: Auto-login is disabled to prevent double biometric prompts.
 * Users should use the Face ID/Touch ID button on the sign-in page instead.
 */
export const initializeAuthPersistence = async () => {
  if (!Capacitor.isNativePlatform()) {
    return; // Only needed for native platforms
  }

  console.log('🔐 Initializing biometric auth persistence...');

  // Listen for auth state changes and offer to save credentials
  onAuthStateChanged(auth, async (user) => {
    if (!user) {
      // User logged out - we don't auto-clear credentials in case they want to use biometric next time
    }
  });

  // AUTO-LOGIN DISABLED: We no longer automatically trigger biometrics on app launch.
  // This prevents the double Face ID prompt issue where:
  // 1. App launches and triggers Face ID automatically
  // 2. User cancels or it fails
  // 3. User lands on sign-in page and has to click Face ID button again
  //
  // Now users will only see Face ID when they explicitly click the button on sign-in page.
  console.log('ℹ️ Auto-login disabled - user will use Face ID button on sign-in page');
};

// Export clearAuthToken for backwards compatibility
export const clearAuthToken = clearBiometricCredentials;
export const saveAuthToken = async () => {
  // No-op for backwards compatibility
};
